import {Component, Inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {CreditApiService} from '../api/services/credit.service';
import {CreditBundle, UserBalanceDetail} from '../api/interfaces/credit.interface';
import {EndTrialFrameComponent} from './end-trial-frame.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

export interface CreditsPurchaseDialogData {
  objectToGenerate?: string;
}

@Component({
  selector: 'credits-purchase-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, EndTrialFrameComponent, TranslatePipe],
  styles: [`
    .warning-banner {
      background-color: #fae6e6;
      color: #cc0000;
      text-align: center;
      padding: 10px 16px;
      font-size: 13px;
      margin-bottom: 12px;
      border-radius: 4px;
    }

    .invitation-message {
      text-align: center;
      font-size: 13px;
      color: #666;
      margin-bottom: 16px;
    }

    .bundles-row {
      display: flex;
      flex-direction: row;
      justify-content: center;
      gap: 12px;
      margin-bottom: 16px;
      flex-wrap: wrap;
    }

    .bundle-card {
      border: 1px solid #ccc;
      background: #fff;
      border-radius: 8px;
      padding: 16px 20px;
      cursor: pointer;
      text-align: center;
      min-width: 120px;
      transition: border-color 0.15s, background-color 0.15s;
    }

    .bundle-card:hover {
      border-color: #f1c232;
    }

    .bundle-card.selected {
      border: 2px solid #f1c232;
      background-color: #fff9e6;
    }

    .bundle-name {
      color: #6aa84f;
      font-family: 'Oswald', sans-serif;
      font-weight: 400;
      font-size: 16px;
      min-height: 1.2em;
    }

    .bundle-credits {
      color: #f1c232;
      font-family: 'Oswald', sans-serif;
      font-weight: 600;
      font-size: 28px;
      margin-top: 4px;
    }

    .bundle-price {
      color: #333;
      font-family: 'Oswald', sans-serif;
      font-weight: 600;
      font-size: 22px;
      margin-top: 4px;
    }

    .bundle-discount {
      color: #6aa84f;
      font-family: 'Oswald', sans-serif;
      font-weight: 400;
      font-size: 16px;
      min-height: 1.2em;
      margin-top: 4px;
    }

    .confirmation-message {
      text-align: center;
      font-size: 13px;
      color: #666;
      margin-bottom: 12px;
    }

    .stripe-badge {
      text-align: center;
      margin-bottom: 16px;
    }

    .stripe-badge img {
      height: 20px;
    }

    .buttons-row {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-bottom: 8px;
    }

    .btn-cancel {
      background: #fff;
      border: 1.5px solid #3D85C6;
      font-family: 'Oswald', sans-serif;
      font-size: 15px;
      text-transform: uppercase;
      border-radius: 6px;
      height: 29px;
      padding: 0 24px;
      cursor: pointer;
      color: #3D85C6;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
    }

    .btn-purchase {
      background: #3D85C6;
      border: none;
      color: #fff;
      font-family: 'Oswald', sans-serif;
      font-size: 15px;
      text-transform: uppercase;
      border-radius: 6px;
      height: 29px;
      padding: 0 24px;
      cursor: pointer;
      line-height: 29px;
    }

    .btn-purchase:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .error-message {
      text-align: center;
      color: #cc0000;
      font-size: 13px;
      margin-top: 8px;
    }

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 40px;
    }
  `],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="icon-smap-credit-purchase me-2"></span>{{ 'CREDITS.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="cancel()"></span>
    </div>

    <mat-dialog-content>
      @if (loading) {
        <div class="loading-container">
          <mat-spinner diameter="36"></mat-spinner>
        </div>
      } @else {
        @if (objectToGenerate) {
          <div class="warning-banner">
            {{ 'CREDITS.PURCHASE_WARNING' | translate: { object: objectToGenerate } }}
          </div>
        }

        <maporium-end-trial-frame (trialEnded)="onTrialEnded($event)"></maporium-end-trial-frame>

        <div class="invitation-message">
          {{ 'CREDITS.INVITATION' | translate }}
        </div>

        <div class="bundles-row">
          @for (bundle of bundles; track bundle.id) {
            <div class="bundle-card"
                 [class.selected]="selectedBundle?.id === bundle.id"
                 (click)="selectBundle(bundle)">
              <div class="bundle-name">{{ bundle.label || '&nbsp;' }}</div>
              <div class="bundle-credits">{{ formatCredits(bundle.creditCount) }}</div>
              <div class="bundle-price">{{ formatPrice(bundle.priceUsd) }}</div>
              <div class="bundle-discount">
                @if (getDiscount(bundle) !== null) {
                  {{ 'V2_ACCOUNT.SAVE_PERCENT' | translate: { percent: getDiscount(bundle) } }}
                } @else {
                  &nbsp;
                }
              </div>
            </div>
          }
        </div>

        @if (selectedBundle) {
          <div class="confirmation-message">
            {{ 'CREDITS.CONFIRMATION' | translate: { count: formatCredits(selectedBundle.creditCount), price: formatPrice(selectedBundle.priceUsd) } }}
          </div>
        }

        <div class="stripe-badge">
          <img src="assets/powered_by_stripe.svg" [alt]="'USER_ACCOUNT.STRIPE_ALT' | translate" />
        </div>

        <div class="buttons-row">
          @if (purchasing) {
            <mat-spinner diameter="24"></mat-spinner>
          } @else {
            <button class="btn-cancel" (click)="cancel()">{{ 'CREDITS.CANCEL' | translate }}</button>
            <button class="btn-purchase" [disabled]="!selectedBundle" (click)="purchase()">{{ 'CREDITS.PURCHASE' | translate }}</button>
          }
        </div>

        @if (error) {
          <div class="error-message">{{ error }}</div>
        }
      }
    </mat-dialog-content>
  `,
})
export class CreditsPurchaseDialogComponent implements OnInit {
  bundles: CreditBundle[] = [];
  selectedBundle: CreditBundle | null = null;
  loading = false;
  purchasing = false;
  error: string | null = null;
  objectToGenerate: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<CreditsPurchaseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreditsPurchaseDialogData | null,
    private creditApiService: CreditApiService,
    private translate: TranslateService,
  ) {
    this.objectToGenerate = data?.objectToGenerate ?? null;
  }

  ngOnInit() {
    this.loading = true;
    this.creditApiService.getBundles().subscribe({
      next: (bundles) => {
        this.bundles = bundles;
        this.selectedBundle = bundles.find(b => b.isDefault) ?? bundles[0] ?? null;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  selectBundle(bundle: CreditBundle) {
    this.selectedBundle = bundle;
  }

  getDiscount(bundle: CreditBundle): number | null {
    if (!this.bundles.length || bundle === this.bundles[0]) return null;
    const baseline = this.bundles[0];
    const baselineCostPerCredit = Number(baseline.priceUsd) / baseline.creditCount;
    const equivalentCost = baselineCostPerCredit * bundle.creditCount;
    const discount = (1 - Number(bundle.priceUsd) / equivalentCost) * 100;
    return Math.floor(discount);
  }

  formatCredits(count: number): string {
    return count.toLocaleString();
  }

  formatPrice(price: number): string {
    return '$' + Number(price).toFixed(2);
  }

  purchase() {
    if (!this.selectedBundle || this.purchasing) return;
    this.purchasing = true;
    this.error = null;
    this.creditApiService.purchaseBundle(this.selectedBundle.id).subscribe({
      next: (balance) => {
        this.purchasing = false;
        this.dialogRef.close(balance);
      },
      error: (err) => {
        this.purchasing = false;
        this.error = err?.error?.message || this.translate.instant('CREDITS.PAYMENT_FAILED');
      }
    });
  }

  onTrialEnded(balance: UserBalanceDetail | undefined) {
    // Ending the trial grants the monthly free credits, so close the purchase
    // dialog and hand the refreshed balance back to the caller.
    this.dialogRef.close(balance ?? null);
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
